/**
 * Value type system for node parameter representations
 */

export type ValueType = "scalar" | "vector3d" | "geometry" | "list"

export type VectorValue = {
	x: number
	y: number
	z: number
}

// TODO: Replace string matching with shared/server-provided parameter kinds.
const VECTOR_TYPES = new Set(["point3d", "vector3d"])
const NUMERIC_SCALAR_TYPES = new Set([
	"double",
	"float",
	"number",
	"int",
	"integer",
])
const BOOLEAN_SCALAR_TYPES = new Set(["bool", "boolean"])
const SCALAR_TYPES = new Set([
	...NUMERIC_SCALAR_TYPES,
	...BOOLEAN_SCALAR_TYPES,
	"string",
])
const LIST_TYPES = new Set(["list"])

function normalizeParamType(paramType: string) {
	return paramType.trim().toLowerCase()
}

export function getValueType(paramType: string): ValueType {
	const normalizedType = normalizeParamType(paramType)

	if (VECTOR_TYPES.has(normalizedType)) return "vector3d"
	if (SCALAR_TYPES.has(normalizedType)) return "scalar"
	if (LIST_TYPES.has(normalizedType)) return "list"
	return "geometry"
}

export function isVectorType(paramType: string): boolean {
	return VECTOR_TYPES.has(normalizeParamType(paramType))
}

export function isScalarType(paramType: string): boolean {
	return SCALAR_TYPES.has(normalizeParamType(paramType))
}

export function isNumericScalarType(paramType: string): boolean {
	return NUMERIC_SCALAR_TYPES.has(normalizeParamType(paramType))
}

export function isBooleanScalarType(paramType: string): boolean {
	return BOOLEAN_SCALAR_TYPES.has(normalizeParamType(paramType))
}

export function isVectorValue(value: unknown): value is VectorValue {
	if (!value || typeof value !== "object") return false
	const v = value as Record<string, unknown>
	return (
		typeof v.x === "number" &&
		typeof v.y === "number" &&
		typeof v.z === "number"
	)
}

export function toVectorValue(
	value: unknown,
	defaultValue: VectorValue = { x: 0, y: 0, z: 0 },
): VectorValue {
	if (isVectorValue(value)) {
		return value
	}
	return defaultValue
}
